import React, { type CSSProperties, memo, ReactNode } from 'react';
import styled from 'styled-components';
import { Flex, Text } from '@mantine/core';
import { DocumentTypeBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/DocumentTypeBadge.tsx';
import { useSmartFieldsNavigation } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/KycDocumentCard/PreviewFormTemplateModal/useSmartFieldsNavigation.ts';
import { FormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { PdfViewerModal } from '@dealstack/frontend/shared/components/DocumentViewerV2/PdfViewer/PdfViewerModal';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';

interface Props {
	formTemplate: FormTemplate;
	opened: boolean;
	fileBlob?: Blob;
	onClose: () => void;
}

const PreviewFormTemplateModal = ({ opened, onClose, formTemplate, fileBlob }: Props) => {
	const tMipCreate = useTranslationMipCreate();

	const { documentViewRef, onDocumentClick } = useSmartFieldsNavigation();

	const formDetails = (
		<Flex direction="column" gap="1rem" style={{ flexGrow: 1, overflow: 'hidden' }}>
			<TitleValuePair title={tMipCreate('kyc.documents.preview_form.classification')}>
				<DocumentTypeBadge
					text={
						formTemplate.type === CustomDetailEnum.INDIVIDUAL
							? tMipCreate('kyc.documents.edit_drawer.individual_investor')
							: tMipCreate('kyc.documents.edit_drawer.piv_investor')
					}
					iconVariant="folder"
				/>
			</TitleValuePair>
			<TitleValuePair title={tMipCreate('kyc.documents.preview_form.file_name')}>
				<Text size="B5Medium">{formTemplate.fileName}</Text>
			</TitleValuePair>
			<TitleValuePair title={tMipCreate('kyc.documents.preview_form.document_tittle')}>
				<Text size="B5Medium">{formTemplate.name}</Text>
			</TitleValuePair>
			<TitleValuePair
				title={tMipCreate('kyc.documents.preview_form.instructions_to_investors')}
			>
				<Text size="B5Medium">{formTemplate.instruction || LONG_DASH}</Text>
			</TitleValuePair>
		</Flex>
	);

	return (
		<PdfViewerModal
			ref={documentViewRef}
			opened={opened}
			onClose={onClose}
			pdfViewerProps={{
				file: fileBlob,
				fileName: formTemplate.name,
				showDetailsByDefault: true,
				renderForms: true,
				onDocumentClick,
				details: formDetails,
			}}
		/>
	);
};

const TitleValuePair = styled(
	({
		title,
		children,
		className,
		style,
	}: {
		title: string;
		children: ReactNode;
		className?: string;
		style?: CSSProperties;
	}) => {
		return (
			<Flex className={className} direction="column" gap="0.5rem" style={style}>
				<Text size="B6Medium" c="gray.9">
					{title}
				</Text>
				{children}
			</Flex>
		);
	}
)``;

const _PreviewFormTemplateModalExport = memo(PreviewFormTemplateModal);

export { _PreviewFormTemplateModalExport as PreviewFormTemplateModal };
